/* Desktop headline price — keep it in step with the chosen buckle.
   HEllO EDC, 9 Sep 2026. Loaded from wc-buckle-drawer.liquid, both stores.

   THE PROBLEM

   Choose an upgrade buckle and the drawer reads 'Rs.2350 with this buckle',
   the cart is right and checkout is right — but the big price at the top of
   the desktop product page still shows the strap-only figure. Two different
   numbers for the same thing, on the same screen.

   WHY

   wc-buckle.js DOES repaint the headline price. Its paintPrice() does:

     var block = document.querySelector('product-info .price');
     if (block) { ...write .price__regular .price-item--regular... }

   That matches the theme's stock price block from snippets/price.liquid.
   But on templates/product.json the stock `price` block is switched OFF in
   the theme editor. The title and price at the top come instead from a
   custom block, product_title_price, in sections/main-product.liquid:

     <div class="custom-product-title-price">
       <h2 class="custom-product-title">{{ product.title }}</h2>
       <div class="custom-product-price">
         {{ product.selected_or_first_available_variant.price | money }}
       </div>
     </div>

   No `price` class, no `price__regular`, no `price-item--regular`. So
   `product-info .price` matches nothing, the `if (block)` guard fails
   quietly, and the headline is never touched. Mobile is unaffected because
   that path uses querySelectorAll on .product-banner-section__title, which
   does exist.

   THE FIX

   Paint .custom-product-price from the same two numbers wc-buckle.js uses.
   It is deliberately a separate file rather than an edit to paintPrice():
   nothing here touches .price or .product-banner-section__title, so the two
   cannot race — they own different elements.

   NOTE ON MONEY FORMATTING

   This does NOT reuse money() from wc-buckle.js / wc-cart.js. That helper
   has no thousands separator, so it renders 2350 as 'Rs.2350' where the
   theme renders 'Rs. 1,950'. Sitting directly under the product title, that
   difference is glaring. The formatter here honours the shop's real money
   format, separators and all. The same defect is why the cart drawer shows
   '$2550' instead of '$2,550' on the international store — worth fixing in
   those two files as well, but not on launch day.

   Does nothing at all when .custom-product-price is absent, so products on
   the other product templates (which keep the stock price block, and which
   wc-buckle.js already handles correctly) are untouched. */

(function () {
  'use strict';

  if (window.__wcPriceSync) return;
  window.__wcPriceSync = true;

  var TARGET = '.custom-product-price';
  var lastWritten = null;

  /* ---------- money ---------- */

  function decode(s) {
    try {
      var t = document.createElement('textarea');
      t.innerHTML = s;
      return t.value;
    } catch (e) { return s; }
  }

  function group(cents, decimals, thousands, point) {
    var n = decimals
      ? (cents / 100).toFixed(2)
      : String(Math.round(cents / 100));
    var parts = n.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decimals ? parts[0] + point + parts[1] : parts[0];
  }

  /* Every placeholder Shopify's money filter understands, so this stays
     correct if either store's currency or format is ever changed. */
  function formatMoney(cents, fmt) {
    fmt = fmt || '${{amount}}';
    return decode(fmt).replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      switch (name) {
        case 'amount':                                    return group(cents, 2, ',', '.');
        case 'amount_no_decimals':                        return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator':               return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':   return group(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':          return group(cents, 2, "'", '.');
        case 'amount_with_space_separator':               return group(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':   return group(cents, 0, ' ', '.');
        default:                                          return group(cents, 2, ',', '.');
      }
    });
  }

  function moneyFormat() {
    var sources = ['[data-wc-buckle-data]', '[data-wc-cart-cfg]'];
    for (var i = 0; i < sources.length; i++) {
      try {
        var el = document.querySelector(sources[i]);
        if (!el) continue;
        var cfg = JSON.parse(el.textContent);
        if (cfg && cfg.moneyFormat) return cfg.moneyFormat;
      } catch (e) { /* try the next one */ }
    }
    return (window.Shopify && window.Shopify.money_format) || null;
  }

  /* ---------- the two numbers ---------- */

  /* The strap, straight from the theme's own selected-variant JSON — the
     same source wc-buckle.js reads, so the two can never disagree. */
  function strapPrice() {
    try {
      var el = document.querySelector('variant-selects script[data-selected-variant]');
      if (!el) return null;
      var v = JSON.parse(el.textContent);
      return typeof v.price === 'number' ? v.price : null;
    } catch (e) { return null; }
  }

  /* The buckle. Its variant price IS the difference: the default buckle is
     priced at zero because it is already included in the strap, and only an
     upgrade carries a figure. */
  function bucklePrice() {
    var wc = window.WCBuckle;
    return wc && typeof wc.componentPrice === 'number' ? wc.componentPrice : 0;
  }

  function paint() {
    var el = document.querySelector(TARGET);
    if (!el) return;

    var strap = strapPrice();
    if (strap === null) return;

    var text = formatMoney(strap + bucklePrice(), moneyFormat());
    if (el.textContent.trim() === text) return;   /* never rewrite what is right */

    el.textContent = text;
    lastWritten = text;
  }

  /* ---------- when to paint ---------- */

  /* wc-buckle.js fires this at the end of every sync() — buckle chosen,
     variant changed, drawer confirmed. */
  document.addEventListener('wc-buckle:change', paint);

  /* The theme re-renders the product section on a variant change, which
     replaces this element with a server-rendered strap-only price. Repaint
     when that happens. Guarded by the equality check in paint(), so our own
     write cannot feed this back into itself. */
  function watch() {
    var host = document.querySelector('product-info');
    if (!host) return;
    try {
      new MutationObserver(function () {
        var el = document.querySelector(TARGET);
        if (!el) return;
        if (lastWritten !== null && el.textContent.trim() === lastWritten) return;
        paint();
      }).observe(host, { childList: true, subtree: true, characterData: true });
    } catch (e) { /* no-op */ }
  }

  function start() { watch(); paint(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
